// GitHub REST client — browser-direct, token-authenticated.
//
// Everything here runs in the creator's browser talking straight to
// api.github.com. No BKA server sits in the path; the token never
// leaves the browser except to GitHub itself (per the philosophy doc).
//
// v1 auth is a pasted token (classic PAT with `repo` + `workflow`
// scopes, or a fine-grained token with equivalent access). GitHub App
// OAuth is deferred — its code→token exchange needs a client secret,
// which would force a backend and break the browser-only constraint.
// See 13_OPEN_QUESTIONS.md.

const API = "https://api.github.com";
const ACCEPT = "application/vnd.github+json";

export interface GithubIdentity {
  login: string;
  name: string | null;
  avatarUrl: string | null;
  /** Scopes GitHub reports for this token (classic PATs only; empty
   *  for fine-grained tokens, which don't expose them this way). */
  scopes: string[];
}

export class GithubError extends Error {
  constructor(message: string, readonly status: number, readonly body?: unknown) {
    super(message);
    this.name = "GithubError";
  }
}

function headers(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    Accept: ACCEPT,
    "X-GitHub-Api-Version": "2022-11-28",
  };
}

async function readError(res: Response): Promise<never> {
  let body: unknown;
  let msg = `GitHub API ${res.status}`;
  try {
    body = await res.json();
    if (body && typeof body === "object" && "message" in body) {
      msg = String((body as { message: unknown }).message);
    }
  } catch { /* non-JSON body */ }
  throw new GithubError(msg, res.status, body);
}

/** Validate a token by fetching the authenticated user. Returns the
 *  identity + (for classic PATs) the scopes the token carries. Throws
 *  GithubError(401) if the token is invalid. */
export async function validateToken(token: string): Promise<GithubIdentity> {
  const res = await fetch(`${API}/user`, { headers: headers(token) });
  if (!res.ok) await readError(res);
  const user = await res.json() as { login: string; name: string | null; avatar_url: string | null };
  // GitHub exposes X-OAuth-Scopes (CORS-allowed via Access-Control-Expose-Headers).
  const scopeHeader = res.headers.get("x-oauth-scopes") ?? "";
  const scopes = scopeHeader.split(",").map((s) => s.trim()).filter(Boolean);
  return {
    login: user.login,
    name: user.name,
    avatarUrl: user.avatar_url,
    scopes,
  };
}

/** True if the token can create repos. Classic PATs advertise scopes,
 *  so we can check `repo` up front; fine-grained tokens report no
 *  scopes, so we optimistically allow and let creation surface any
 *  permission error with GitHub's own message. */
export function canCreateRepos(identity: GithubIdentity): boolean {
  if (identity.scopes.length === 0) return true; // fine-grained: can't tell, try anyway
  return identity.scopes.includes("repo");
}

/** Does a repo already exist on this owner's account? Used by the
 *  handle picker to confirm both target repo names are free. */
export async function repoExists(token: string, owner: string, repo: string): Promise<boolean> {
  const res = await fetch(`${API}/repos/${owner}/${repo}`, { headers: headers(token) });
  if (res.status === 404) return false;
  if (res.ok) return true;
  await readError(res);
  return false; // unreachable
}

export interface CreatedRepo {
  fullName: string;
  htmlUrl: string;
}

/** Create a repo in the authenticated user's account. `auto_init`
 *  gives us a default branch + initial commit so subsequent Contents
 *  API writes have something to branch from. Idempotent-ish: if the
 *  repo already exists GitHub returns 422, which the caller treats as
 *  "already provisioned" during a resume. */
export async function createRepo(
  token: string,
  opts: { name: string; description: string; private: boolean },
): Promise<CreatedRepo> {
  const res = await fetch(`${API}/user/repos`, {
    method: "POST",
    headers: { ...headers(token), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: opts.name,
      description: opts.description,
      private: opts.private,
      auto_init: true,
      has_issues: true,
      has_wiki: false,
      has_projects: false,
    }),
  });
  if (!res.ok) await readError(res);
  const repo = await res.json() as { full_name: string; html_url: string };
  return { fullName: repo.full_name, htmlUrl: repo.html_url };
}

/** Pre-scoped token-creation deep link. Lands the creator on GitHub's
 *  "new classic token" page with exactly the scopes BKA needs
 *  pre-checked — the "one click, right permissions" UX from doc 04,
 *  applied to the token path instead of OAuth. */
export function newTokenUrl(): string {
  const params = new URLSearchParams({
    description: "BKA — Bender's Killer App",
    scopes: "repo,workflow",
  });
  return `https://github.com/settings/tokens/new?${params.toString()}`;
}
